import { FC, useContext, useMemo, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Alert } from "../../../../components/attentions";
import { H2 } from "../../../../components/basics";
import SkeletonText from "../../../../components/basics/SkeletonText";
import { IOption } from "../../../../components/form-fields/RadioInput";
import { limitValidations } from "../../../../helpers/professional_indemnity/validations";
import useForm from "../../../../hooks/useForm";
import classNamePresets from "../../../../utils/classNamePresets";
import LimitRadioGroup from "../../components/LimitRadioGroup";
import { IndemnityFormContext } from "../../NewProduct";
import IndemnityFormStepNavigation from "../IndemnityFormStepNavigation";
import { LimitState } from "../types";
import clientApiService from "../../../../services/professional_indemnity/clientApiService";
import { useQuery } from "react-query";
import PremiumLabel from "../../../components/PremiumLabel";
import { formatToServerDate } from "../../../../utils/date";
import { formatToTwoDecimalsWithThousandSeparator } from "../../../../helpers/shared/format";
import { MPIMultiLimitPriceResponse } from "../../../../api/professional-indemnity/PricingApi";
import ServerError from "../../../errors/ServerError";

type Props = {
  onNext: VoidFunction | null;
  onPrevious: VoidFunction | null;
  isNewTranslationFlow: boolean;
};

const TotalPremiumLabel: FC<{ premium: number }> = ({ premium }) => (
  <PremiumLabel label="Total Premium (incl. 0.1% IA Levy)" value={formatToTwoDecimalsWithThousandSeparator(premium)} currency="HKD" />
);

const options: IOption<LimitState["limit"]>[] = [
  {
    value: 3_500_000,
    label: "HKD 3,500,000",
  },
  {
    value: 7_500_000,
    label: "HKD 7,500,000",
  },
  {
    value: 12_500_000,
    label: "HKD 12,500,000",
  },
];

const getSvOptions = (isLoading: boolean, data: MPIMultiLimitPriceResponse | undefined): IOption<LimitState["limit"]>[] => [
  {
    value: 5_000_000,
    label: "HKD 5,000,000 港幣五百萬",
    rightLabel: isLoading ? <SkeletonText /> : <TotalPremiumLabel premium={Number(data?.["5000000"]?.total ?? 0)} />,
  },

  {
    value: 10_000_000,
    label: "HKD 10,000,000 港幣一千萬",
    rightLabel: isLoading ? <SkeletonText /> : <TotalPremiumLabel premium={Number(data?.["10000000"]?.total ?? 0)} />,
  },
];

const Limit: FC<Props> = ({ onNext, onPrevious, isNewTranslationFlow }) => {
  const { t } = useTranslation("pages", {
    keyPrefix: "professionalIndemnity.newProduct",
  });

  const { state, onChange, clientId } = useContext(IndemnityFormContext);
  const form = useForm(state, limitValidations, onChange);
  const [serverError, setServerError] = useState(false);

  const getManulifeSvPricing = async (): Promise<MPIMultiLimitPriceResponse> => {
    return clientApiService.getPricing(clientId, {
      coverageStartDate: formatToServerDate(state.coverageStartDate),
      coverageEndDate: formatToServerDate(state.coverageEndDate),
      isManulifeSvPricingLimits: true,
    });
  };

  const query = useQuery(["sv-pricing", clientId, state.coverageStartDate, state.coverageEndDate], getManulifeSvPricing, {
    enabled: isNewTranslationFlow && !!state.coverageStartDate && !!state.coverageEndDate,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  useEffect(() => {
    if (query.isLoadingError) {
      setServerError(true);
    }
  }, [query.isLoadingError]);

  const svOptions = getSvOptions(query.isLoading, query.data);

  const lolOptions = useMemo(() => (isNewTranslationFlow ? svOptions : options), [isNewTranslationFlow, svOptions]);
  const {
    field: { limit },
  } = form;

  const isPricingBlocked = isNewTranslationFlow && (query.isLoading || query.isLoadingError);

  const handleNext = () => {
    const canProceed = !isPricingBlocked && form.validateAll();
    if (canProceed) onNext?.();
  };

  return (
    <>
      <div className="container-flow flex-1">
        <H2 className={classNamePresets.mainHeading}>{t("steps.limit")}</H2>
        <LimitRadioGroup
          mainClassName="flex justify-between flex-wrap mb-5 md:mb-12 gap-x-20"
          labelClassName="mt-8 flex-shrink max-w-[40rem]"
          fieldsClassName="min-w-[20rem] flex-1"
          state={state}
          options={lolOptions}
          limit={limit}
          ariaLabel="limit of liability"
          limitTranslationObject={t}
        />
        <Alert type="warning" icon="icon-info">
          <p className="space-y-4 whitespace-pre-line">{t("totalAggregateLimit")}</p>
        </Alert>
      </div>

      {serverError && <ServerError supportEmailId={t("customerSupport.emailId")} displayType="modal" onClose={() => setServerError(false)} />}
      <IndemnityFormStepNavigation isLoading={false} onBack={onPrevious} onNext={handleNext} />
    </>
  );
};

export default Limit;
